/**
 * A LINK TO A COURSE THE CATALOGUE DOES NOT LIST (job #192).
 *
 * The catalogue is `new_app_status in (live, beta)`, read the same for
 * everybody. A draft course kept out of it — the Afrikaans re-chunk pilot,
 * `afr_pilot_for_eng` — still has to be playable by the people building it,
 * on staging, which shares its database with live. Flipping it to beta would
 * put it in every learner's picker on live too.
 *
 * So a `?course=` / `/learn/<code>` link naming a code the catalogue does not
 * hold asks for that ONE row with the visitor's own session. The `courses`
 * select policy already decides who sees a hidden row: an ssi_admin, a
 * dashboard user, or a learner enrolled in it. Everybody else gets no row
 * and lands exactly where an unknown link always landed. Nothing here widens
 * what anybody can read; it only stops the app ignoring a row RLS returned.
 *
 * A row found this way opens the course for this visit and nothing more: it
 * is not added to the catalogue, its mirror, its stamp or the picker, and it
 * is not remembered as the visitor's course (App reads `source`).
 */


export type LinkedCourseSource = 'catalogue' | 'preview'

export interface LinkedCourse<T> {
  course: T
  source: LinkedCourseSource
}

/** Fetch one course row the caller's session may see, or null. */
export type FetchCourseRow<T> = (courseCode: string) => Promise<T | null>

export interface PreviewWho {
  platformRole: string | null
  /** Popty dashboard staff: the answer of the `is_dashboard_user()` RPC, the same test RLS uses. */
  isDashboardUser: boolean
  /** Course codes the learner has an enrolment row for. */
  enrolledCodes: Iterable<string>
}

/**
 * Who may open a course the catalogue does not list: an SSi admin, a
 * dashboard user (a Popty `dashboard_users` row, NOT a school role: a teacher
 * with no enrolment is refused, a dashboard user with no role is let in) or a learner enrolled in it. A row coming
 * back from the database is NOT proof of any of these: a draft course can
 * carry visibility public, and then every visitor's read returns it (job
 * #214). Only the visitor's standing counts.
 */
export function mayPreviewOutsideCatalogue(courseCode: string, who: PreviewWho): boolean {
  if (who.platformRole === 'ssi_admin') return true
  if (who.isDashboardUser) return true
  for (const code of who.enrolledCodes) if (code === courseCode) return true
  return false
}

/**
 * The same question, asked where enrolments may not have loaded yet. Enrolments
 * arrive on a watch on the learner id, so on a cold boot they can still be in
 * flight when the link is checked, and an enrolled learner landed on the
 * picker. The load is awaited only when it can change the answer: the course
 * is not in the catalogue and the role checks have not already passed.
 */
export async function mayPreviewOutsideCatalogueAwaiting(
  courseCode: string,
  catalogue: readonly { course_code: string }[],
  who: { platformRole: string | null },
  loadEnrolledCodes: () => Promise<Iterable<string>>,
  loadIsDashboardUser: () => Promise<boolean>,
): Promise<boolean> {
  if (catalogue.some(c => c.course_code === courseCode)) return false
  if (mayPreviewOutsideCatalogue(courseCode, { ...who, isDashboardUser: false, enrolledCodes: [] })) return true
  // Asked of the database, as RLS asks it: the role stores do not know dashboard membership.
  const isDashboardUser = (await loadIsDashboardUser().catch(() => false)) === true
  if (isDashboardUser) return true
  const codes = await loadEnrolledCodes()
  // A loader that timed out may hand back a non-iterable sentinel: read it as no enrolments.
  const enrolledCodes = codes && typeof (codes as Iterable<string>)[Symbol.iterator] === 'function' ? codes : []
  return mayPreviewOutsideCatalogue(courseCode, { ...who, isDashboardUser, enrolledCodes })
}

export async function resolveLinkedCourse<T extends { course_code: string }>(
  courseCode: string,
  catalogue: readonly T[],
  fetchRow: FetchCourseRow<T>,
  mayPreview: boolean,
): Promise<LinkedCourse<T> | null> {
  const listed = catalogue.find(c => c.course_code === courseCode)
  if (listed) return { course: listed, source: 'catalogue' }
  if (!mayPreview) return null
  let row: T | null = null
  try {
    row = await fetchRow(courseCode)
  } catch {
    row = null
  }
  return row && row.course_code === courseCode ? { course: row, source: 'preview' } : null
}

/** Is the caller in `dashboard_users`? The RPC RLS itself uses; any failure reads as no. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function isDashboardUserRpc(client: any): Promise<boolean> {
  const { data, error } = await client.rpc('is_dashboard_user')
  return !error && data === true
}

/**
 * The one-row read: it decides which course the first sound comes from. When
 * the boot DB door held every read it had not been told about, this one waited
 * out the network budget and every link landed on the picker (job #192). The
 * door is gone since job #271.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function courseRowQuery(client: any, courseCode: string, columns: string) {
  return client.from('courses').select(columns).eq('course_code', courseCode).maybeSingle()
}

/**
 * Is this learner enrolled in this one course? One row, not the all-course
 * enrolment list, because this check decides which course the first sound
 * comes from.
 * Resolves to the enrolled codes (zero or one).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function enrolledCodesForCourse(client: any, learnerId: string, courseCode: string): Promise<string[]> {
  const { data, error } = await client.from('course_enrollments').select('course_id')
    .eq('learner_id', learnerId)
    .eq('course_id', courseCode)
  if (error) return []
  return (data || []).map((r: { course_id: string }) => r.course_id)
}
